import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api/v1';

export interface MFASetupResponse {
  success: boolean;
  data: {
    secret: string;
    qrCode: string;
    backupCodes: string[];
    message: string;
  };
}

export interface MFAStatusResponse {
  success: boolean;
  data: {
    mfaEnabled: boolean;
  };
}

export interface MFAResponse {
  success: boolean;
  message?: string;
  error?: string;
}

class MFAService {
  /**
   * Get MFA status for current user
   */
  async getMFAStatus(): Promise<boolean> {
    try {
      const response = await axios.get<MFAStatusResponse>(
        `${API_URL}/auth/mfa/status`,
        { withCredentials: true }
      );
      return response.data.data.mfaEnabled;
    } catch (error) {
      console.error('Error getting MFA status:', error);
      throw error;
    }
  }

  /**
   * Setup MFA - Generate secret and QR code
   */
  async setupMFA(): Promise<MFASetupResponse['data']> {
    try {
      const response = await axios.post<MFASetupResponse>(
        `${API_URL}/auth/mfa/setup`,
        {},
        { withCredentials: true }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error setting up MFA:', error);
      throw new Error(error.response?.data?.error || 'Failed to setup MFA');
    }
  }

  /**
   * Verify and enable MFA with TOTP token
   */
  async verifyAndEnableMFA(token: string): Promise<string> {
    try {
      const response = await axios.post<MFAResponse>(
        `${API_URL}/auth/mfa/verify`,
        { token },
        { withCredentials: true }
      );
      return response.data.message || 'MFA enabled successfully';
    } catch (error: any) {
      console.error('Error verifying MFA:', error);
      throw new Error(error.response?.data?.error || 'Failed to verify MFA');
    }
  }

  /**
   * Disable MFA with password confirmation
   */
  async disableMFA(password: string): Promise<string> {
    try {
      const response = await axios.post<MFAResponse>(
        `${API_URL}/auth/mfa/disable`,
        { password },
        { withCredentials: true }
      );
      return response.data.message || 'MFA disabled successfully';
    } catch (error: any) {
      console.error('Error disabling MFA:', error);
      throw new Error(error.response?.data?.error || 'Failed to disable MFA');
    }
  }

  /**
   * Validate MFA token during login
   */
  async validateMFA(userId: string, token: string): Promise<{ token: string; user: any }> {
    try {
      console.log('[MFA Service] Validating MFA for user:', userId, 'with token:', token);
      const response = await axios.post(
        `${API_URL}/auth/mfa/validate`,
        { userId, token },
        { withCredentials: true }
      );
      console.log('[MFA Service] Validation response:', response.data);
      
      // Backend returns { success: true, token: "...", user: {...} }
      // Extract just the token and user
      if (response.data.success) {
        return {
          token: response.data.token,
          user: response.data.user
        };
      }
      
      throw new Error('MFA validation failed');
    } catch (error: any) {
      console.error('[MFA Service] Error validating MFA:', error);
      console.error('[MFA Service] Error response:', error.response?.data);
      throw new Error(error.response?.data?.error || 'Invalid MFA code');
    }
  }
}

export default new MFAService();
